/**
 * Transcribe audio recording via the same API as mobile.
 * Uploads audio to POST /api/transcriptions and returns { text, language?, duration? }.
 */

const getApiUrl = () => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
  return base.endsWith('/') ? base.slice(0, -1) : base;
};

/**
 * @param {Object} params
 * @param {Blob} params.audioBlob - Recorded audio blob (e.g. from MediaRecorder)
 * @param {string} [params.fileName] - Optional filename
 * @param {string} [params.mimeType] - Optional mime type (default from blob)
 * @param {AbortSignal} [params.signal] - AbortSignal for cancellation
 * @returns {Promise<{ text: string, language?: string, duration?: number }>}
 */
export async function transcribeAudioRecording({ audioBlob, fileName, mimeType, signal }) {
  if (!audioBlob || !(audioBlob instanceof Blob)) {
    throw new Error('Audio blob is required');
  }

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (!token) {
    throw new Error('Not authenticated');
  }

  const resolvedName = fileName || `recording.${getExtensionFromMime(audioBlob.type)}`;
  const resolvedType = mimeType || audioBlob.type || 'audio/webm';

  const formData = new FormData();
  formData.append('audio', audioBlob, resolvedName);

  if (signal?.aborted) {
    const err = new Error('Aborted');
    err.name = 'AbortError';
    throw err;
  }

  try {
    const response = await fetch(`${getApiUrl()}/transcriptions`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
      signal: signal || undefined,
    });

    const parsed = await response.json().catch(() => null);

    if (response.status >= 400 || !parsed) {
      const message =
        (parsed && (parsed.message || parsed.error)) || 'Failed to transcribe audio';
      throw new Error(message);
    }

    return parsed;
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw error;
    }
    if (signal?.aborted) {
      const abortError = new Error('Aborted');
      abortError.name = 'AbortError';
      throw abortError;
    }
    throw error;
  }
}

function getExtensionFromMime(mime) {
  const map = {
    'audio/webm': 'webm',
    'audio/mp4': 'm4a',
    'audio/m4a': 'm4a',
    'audio/ogg': 'ogg',
    'audio/ogg;codecs=opus': 'ogg',
  };
  return map[mime] || 'webm';
}
